/**
 * Branding Configuration Types
 * 
 * Defines the complete type system for white-label branding configuration.
 * These types match the branding.yaml schema served by the BFF API.
 * 
 * @module types/branding
 * @version 1.0.0
 */

/**
 * Logo asset URLs for different themes and contexts
 */
export interface BrandingLogos {
  /** 
   * Primary logo URL (required)
   * Used as default across all themes
   * @example "https://cdn.company.com/logo.png" or "/logo.png"
   */
  primary: string;
  
  /** 
   * Dark theme variant (optional)
   * Used when theme mode is 'black'
   * Falls back to primary if not provided
   */
  dark?: string;
  
  /** 
   * Light theme variant (optional)
   * Used when theme mode is 'white'
   * Falls back to primary if not provided
   */
  light?: string;
  
  /** 
   * Small icon (32x32) for compact displays (optional)
   * Used in tight spaces or as favicon fallback
   */
  icon?: string;
}

/**
 * Custom color overrides for theme customization
 * All colors must be valid CSS color values (hex, rgb, rgba)
 * 
 * Color contrast guidelines (WCAG AA):
 * - Normal text: 4.5:1 contrast ratio
 * - Large text: 3:1 contrast ratio
 * - UI components: 3:1 contrast ratio
 */
export interface BrandingColors {
  /** 
   * Primary brand color
   * Used for: buttons, links, primary actions
   * @example "#0066CC"
   */
  primary: string;
  
  /** 
   * Accent color for highlights
   * Used for: secondary buttons, hover states
   * @example "#FF6B35"
   */
  accent?: string;
  
  /** 
   * Main background color
   * Used for: page background
   * @example "#F5F5F5"
   */
  background?: string;
  
  /** 
   * Primary text color
   * Must have sufficient contrast with background
   * @example "#1A1A1A"
   */
  text?: string;
  
  /** 
   * Secondary text color
   * Used for: helper text, descriptions
   * @example "#666666"
   */
  textSecondary?: string;
  
  /** 
   * Card/panel background color
   * @example "#FFFFFF"
   */
  card?: string;
  
  /** 
   * Border color
   * Used for: dividers, outlines
   * @example "#E0E0E0"
   */
  border?: string;
  
  /** 
   * Button background color
   * @example "#0066CC"
   */
  button?: string;
  
  /** 
   * Button text color
   * Must have sufficient contrast with button background
   * @example "#FFFFFF"
   */
  buttonText?: string;
  
  /** 
   * Success state color
   * Used for: success messages, positive feedback
   * @example "#28A745"
   */
  success?: string;
  
  /** 
   * Error state color
   * Used for: error messages, validation failures
   * @example "#DC3545"
   */
  error?: string;
  
  /** 
   * Warning state color
   * Used for: warnings, caution messages
   * @example "#FFC107"
   */
  warning?: string;
  
  /** 
   * Info state color
   * Used for: informational messages
   * @example "#17A2B8"
   */
  info?: string;
}

/**
 * Theme configuration
 */
export interface BrandingTheme {
  /** 
   * Theme mode
   * - 'blue': NeonFlux theme (default)
   * - 'white': Clean light theme
   * - 'black': Modern dark theme
   * - 'custom': Use custom colors defined in `colors` field
   * - Or any custom theme name from themes.yaml (e.g., 'erste', 'daimler')
   */
  mode: string;
  
  /**
   * Whether users can override the admin-configured theme
   * When false, admin theme is ENFORCED for all users
   * When true (default), users can pick their own theme in Theme Gallery
   * @default true
   */
  allowUserOverride?: boolean;
  
  /**
   * Optional URL to external CSS file with advanced overrides
   * This CSS file can override any CSS variable in the application
   * @example "/api/configs/branding/tenant.css"
   */
  brandCssUrl?: string;
  
  /** 
   * Custom color overrides
   * Only used if mode='custom'
   * If null, theme defaults are used
   */
  colors?: BrandingColors | null;
}

/**
 * Footer link configuration
 */
export interface BrandingFooterLink {
  /** Link label text */
  label: string;
  
  /** Link URL (relative or absolute) */
  url: string;
}

/**
 * Footer configuration
 */
export interface BrandingFooter {
  /** 
   * Footer text (typically copyright notice)
   * @example "© 2025 Acme Corporation"
   */
  text: string;
  
  /** 
   * Optional footer links
   * Displayed next to footer text
   */
  links?: BrandingFooterLink[];
}

/**
 * SEO metadata configuration
 */
export interface BrandingMetadata {
  /** 
   * Page description for SEO
   * Used in <meta name="description">
   */
  description?: string;
  
  /** 
   * Keywords for SEO
   * Used in <meta name="keywords">
   */
  keywords?: string;
}

/**
 * Header chrome overrides (user menu, etc.)
 */
export interface BrandingHeader {
  /** Hide App Center link in the user profile dropdown (NomadID-style fixed deployments) */
  hideAppCenter?: boolean;
  /** Hide My Security link in the user profile dropdown */
  hideMySecurity?: boolean;
}

/**
 * Complete branding configuration
 * 
 * This is the root interface for all branding settings.
 * It matches the structure of branding.yaml in ServiceConfigs.
 */
export interface BrandingConfig {
  /** 
   * Application display name
   * Used in: page titles, header, browser tab
   * @example "Acme Enterprise Portal"
   */
  appName: string;
  
  /** 
   * Short name for compact displays
   * Used when full app name doesn't fit
   * @example "Acme"
   */
  shortName?: string;
  
  /** Logo assets */
  logos: BrandingLogos;
  
  /** 
   * Favicon URL
   * Recommended: 32x32 PNG or ICO format
   */
  favicon?: string;
  
  /** Theme configuration */
  theme?: BrandingTheme;
  
  /** Footer configuration */
  footer?: BrandingFooter;

  /** Header / user menu overrides */
  header?: BrandingHeader;
  
  /** SEO metadata */
  metadata?: BrandingMetadata;
}

/**
 * Branding context value
 * 
 * This is the shape of the value provided by BrandingContext.
 * It includes the config plus loading/error states and utilities.
 */
export interface BrandingContextValue {
  /** 
   * Current branding configuration
   * Null while loading or if failed to load (defaults will be used)
   */
  config: BrandingConfig | null;
  
  /** 
   * Loading state
   * True while initial fetch is in progress
   */
  loading: boolean;
  
  /** 
   * Error state
   * Null if no error, Error object if load failed
   * Note: Errors are non-fatal; app falls back to defaults
   */
  error: Error | null;
  
  /** 
   * Manually refresh branding from API
   * Useful for admin UI or after config changes
   */
  refresh: () => Promise<void>;
}

/**
 * CSS variable mapping
 * Maps branding color keys to CSS variable names
 * @internal
 */
export const CSS_VAR_MAPPING: Record<string, string> = {
  primary: '--main-accent',
  accent: '--button-bg',
  background: '--main-bg',
  text: '--main-text',
  textSecondary: '--main-text-secondary',
  card: '--card-bg',
  border: '--border-color',
  button: '--button-bg',
  buttonText: '--button-text',
  success: '--success-color',
  error: '--error-color',
  warning: '--warning-color',
  info: '--info-color',
};

/**
 * Default branding configuration
 * Used as fallback when API fails or config is missing
 * @internal
 */
export const DEFAULT_BRANDING_CONFIG: BrandingConfig = {
  appName: 'Experience',
  shortName: 'Experience',
  logos: {
    primary: '/en-logo-light.png',
  },
  theme: {
    mode: 'blue',
  },
  footer: {
    text: '© 2026 EmpowerID. All rights reserved.',
  },
};

/**
 * Cache configuration
 * @internal
 */
export const BRANDING_CACHE_CONFIG = {
  /** LocalStorage key for cached branding */
  CACHE_KEY: 'experience-branding-cache',
  
  /** Cache TTL in milliseconds (5 minutes) */
  CACHE_TTL: 5 * 60 * 1000,
} as const;

